/**
 * Adding a picture for one artwork role: choose the file, click what must stay in frame, upload.
 * The browser makes every size the apps use for the role (lib/image-pipeline, the media agent's
 * rules), so nothing needs an image service. Used in an item's Artwork section (the role is fixed),
 * the header's Upload button and Files (the role is chosen).
 */

import { useEffect, useMemo, useState } from "react";
import Button from "@web-ui/components/Button";
import Field from "@web-ui/components/Field";
import Select from "@web-ui/components/Select";
import { cropFor, ROLES, type ImageRole } from "@media-domain/images";
import { media, type MediaAsset } from "@/api/admin";
import type { AdminClient } from "@/api/client";
import { makeImage, sourceSize } from "@/lib/image-pipeline";

export const ROLE_WORDS: Record<ImageRole, string> = {
  poster: "Poster (2:3, tall)", backdrop: "Backdrop (16:9, wide)", thumbnail: "Thumbnail (16:9, small)", square: "Square (1:1)",
  logo: "Title logo (transparent)", hero: "Banner (12:5, very wide)", topShelf: "Apple TV Top Shelf (2320 × 720)", person: "Photo of a person (1:1)",
};
const ROLE_NAMES = Object.keys(ROLES) as ImageRole[];

export function ImageCropUpload({ client, role: fixedRole, defaultTitle, onDone, onError, onCancel }: {
  client: AdminClient;
  /** The role to make; when absent the editor chooses one. */
  role?: ImageRole;
  defaultTitle?: string;
  onDone: (feed: Record<string, unknown>, asset: MediaAsset, existing: boolean) => void;
  onError: (e: unknown) => void;
  onCancel?: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const [role, setRole] = useState<ImageRole>(fixedRole ?? "poster");
  const [focal, setFocal] = useState({ x: 0.5, y: 0.5 });
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");

  useEffect(() => {
    setProblem("");
    if (!file) { setPreview(""); setSize(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    void sourceSize(file).then(setSize).catch(() => setProblem("This file is not a picture the browser can read. Try a JPEG, PNG or WebP."));
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // What the role's crop keeps, as a box over the preview (in percent).
  const box = useMemo(() => {
    if (!size) return null;
    const c = cropFor(size.width, size.height, ROLES[role].aspect, focal);
    return { left: (c.left / size.width) * 100, top: (c.top / size.height) * 100, width: (c.width / size.width) * 100, height: (c.height / size.height) * 100, cropWidth: c.width };
  }, [size, role, focal]);
  const tooSmall = box && box.cropWidth < ROLES[role].minSourceWidth ? `This picture is too small for a ${ROLE_WORDS[role].toLowerCase()}: it needs to be at least ${ROLES[role].minSourceWidth} px wide after cropping, and this one is ${box.cropWidth} px.` : "";

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const made = await makeImage(file, role, focal);
      const input = { title: title || defaultTitle || `${file.name.replace(/\.[^.]+$/, "")} (${role})`, role, sha256: made.sha256, sizes: made.files.map((f) => ({ width: f.width, height: f.height, bytes: f.blob.size })), color: made.color, blurhash: made.blurhash, focal: made.focal };
      const started = await media.startImage(client, input);
      let asset = started.asset;
      if (!started.existing) {
        await Promise.all(started.uploads!.map(async (u) => {
          const blob = made.files.find((f) => f.width === u.width)!.blob;
          const res = await fetch(u.url, { method: "PUT", headers: u.headers, body: blob });
          if (!res.ok) throw new Error(`The ${u.width} px size did not upload (${res.status}). Try again.`);
        }));
        asset = (await media.finishImage(client, started.asset.id, input)).asset;
      }
      if (!asset.feed) throw new Error("The picture was stored but is not ready yet. Choose it from Files in a moment.");
      onDone(asset.feed, asset, !!started.existing);
      setFile(null); setTitle("");
    } catch (e) { onError(e); } finally { setBusy(false); }
  };

  const roleOptions = ROLE_NAMES.map((name, id) => ({ id, name: ROLE_WORDS[name] }));
  return (
    <div className="flex flex-col gap-3">
      <div className={`grid gap-3 ${fixedRole ? "" : "md:grid-cols-2"}`}>
        <label className="flex flex-col gap-1 text-body-2">Picture
          <input type="file" accept="image/*" className="text-body-2" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          {size ? <span className="text-caption text-t-tertiary">{size.width} × {size.height} px</span> : null}
        </label>
        {fixedRole ? null : <Select label="Shape" value={roleOptions[ROLE_NAMES.indexOf(role)]!} options={roleOptions} onChange={(o) => setRole(ROLE_NAMES[o.id]!)} />}
      </div>
      {preview && box ? (
        <>
          <p className="text-body-2 text-t-secondary">Click the picture where its subject is: the {ROLES[role].aspect ? "frame" : "picture"} stays around that point in every size.</p>
          <div className="relative w-full max-w-xl cursor-crosshair select-none overflow-hidden rounded-2xl"
            onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setFocal({ x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) }); }}>
            <img src={preview} alt="The picture to crop" className="block w-full" />
            <div className="pointer-events-none absolute border-2 border-t-primary shadow-[0_0_0_9999px_color-mix(in_srgb,var(--color-b-surface1)_65%,transparent)]" style={{ left: `${box.left}%`, top: `${box.top}%`, width: `${box.width}%`, height: `${box.height}%` }} />
            <div className="pointer-events-none absolute size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-t-primary bg-accent" style={{ left: `${focal.x * 100}%`, top: `${focal.y * 100}%` }} />
          </div>
        </>
      ) : null}
      {problem || tooSmall ? <p className="text-body-2 text-critical" role="alert">{problem || tooSmall}</p> : null}
      {file && !fixedRole ? <Field label="Name in Files" optional value={title} onChange={(e) => setTitle(e.target.value)} /> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="primary" disabled={!file || !!problem || !!tooSmall} isPending={busy} onClick={() => void upload()}>Upload</Button>
        {onCancel ? <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button> : null}
      </div>
    </div>
  );
}
